import { useState, useRef, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Upload, Search, X, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { conciliacionProveedoresApi, proveedoresApi } from '../../../lib/api';

export function UploadDialog({ open, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [notas, setNotas] = useState('');
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Iter 322 — Proveedor obligatorio via combobox contra Maestro de Proveedores
  const [provQuery, setProvQuery] = useState('');
  const [provResults, setProvResults] = useState([]);
  const [provSelected, setProvSelected] = useState(null); // {id, nombre, rut}
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (provSelected || provQuery.trim().length < 2) { setProvResults([]); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await proveedoresApi.list({ q: provQuery.trim(), page_size: 10 });
        setProvResults(res.data?.items || []);
      } catch {
        setProvResults([]);
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [provQuery, provSelected]);

  const reset = () => {
    setFile(null); setNotas(''); setProgress(0);
    setProvQuery(''); setProvResults([]); setProvSelected(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!file) { toast.error('Seleccioná un archivo'); return; }
    if (!provSelected) { toast.error('Selecciona el proveedor del Maestro (busca por razón social o RUT)'); return; }
    setLoading(true); setProgress(10);
    try {
      const res = await conciliacionProveedoresApi.upload(
        file,
        { proveedor_id: provSelected.id, notas },
        (ev) => setProgress(Math.round((ev.loaded / ev.total) * 70)),
      );
      setProgress(100);
      toast.success(`✓ Procesado: ${res.data?.totales?.match_perfecto || 0} OK · ${res.data?.totales?.faltante || 0} faltantes`);
      reset();
      onUploaded?.(res.data);
      onClose?.();
    } catch (e2) {
      toast.error(`Error: ${e2.response?.data?.detail || e2.message}`);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-4 h-4" /> Subir estado de cuenta</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium block mb-1">Archivo (PDF, imagen o Excel)</label>
            <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm border rounded p-1.5"
              data-testid="input-conciliacion-file"
            />
            {file && <div className="text-[11px] text-muted-foreground mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">
              Proveedor <span className="text-red-500">*</span>
              <span className="text-muted-foreground font-normal ml-1">(busca por razón social o RUT)</span>
            </label>
            {provSelected ? (
              <div className="flex items-center justify-between gap-2 border rounded px-2.5 py-1.5 bg-emerald-50 border-emerald-300" data-testid="conciliacion-proveedor-selected">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-4 h-4 text-emerald-700 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{provSelected.nombre}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{provSelected.rut}</div>
                  </div>
                </div>
                <button type="button" onClick={() => { setProvSelected(null); setProvQuery(''); }}
                  className="text-muted-foreground hover:text-red-600 shrink-0" data-testid="btn-clear-proveedor">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input
                  value={provQuery}
                  onChange={(e) => setProvQuery(e.target.value)}
                  placeholder="Ej: SODEXO o 76.xxx.xxx-x"
                  className="pl-8"
                  data-testid="input-conciliacion-proveedor-search"
                />
                {(provResults.length > 0 || searching) && (
                  <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-lg max-h-52 overflow-y-auto" data-testid="conciliacion-proveedor-results">
                    {searching && <div className="px-3 py-2 text-xs text-muted-foreground">Buscando…</div>}
                    {provResults.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setProvSelected(p); setProvResults([]); }}
                        className="w-full text-left px-3 py-1.5 hover:bg-muted text-sm flex items-center justify-between gap-2"
                        data-testid={`prov-opcion-${p.id}`}
                      >
                        <span className="truncate">{p.nombre}</span>
                        <span className="text-[11px] text-muted-foreground font-mono shrink-0">{p.rut}</span>
                      </button>
                    ))}
                    {!searching && provResults.length === 0 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">Sin resultados en el Maestro</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Notas</label>
            <Textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} className="text-sm" data-testid="textarea-conciliacion-notas" />
          </div>
          {loading && (
            <div>
              <div className="h-2 bg-muted rounded overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {progress < 70 ? 'Subiendo archivo…' : 'Extrayendo datos con IA…'}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading}>Cancelar</Button>
            <Button type="submit" size="sm" disabled={loading || !file || !provSelected} data-testid="btn-conciliacion-upload-submit">
              {loading ? 'Procesando…' : 'Procesar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
